/**
 * Shared QuestionTypeEnum display names — single source for Gen Question dropdown
 * and stepped import flag annotator.
 *
 * WHY: Both UIs must show the same bilingual labels (not raw Agent slugs). Keeping
 * them in content-prompt lets web-content, shared-components, and the editor share
 * one catalog without duplicating en/vi strings.
 *
 * EAA-2026: also the display-name source for the legacy QT_* (Tạo nhanh) types, so the
 * program-tree bulk dialog and the AI generator read one table. Group headings are NOT here:
 * they are the tenant's question-type groups (see questionTypeGroups.ts).
 */

import type { QuickCreateCode } from './quickCreateCatalog';

export type QuestionTypeEnumSlug =
    | 'set'
    | 'passage'
    | 'audio'
    | 'image'
    | 'scq'
    | 'mcq'
    | 'true_false'
    | 'gap_typing'
    | 'gap_dropdown'
    | 'gap_word_bank'
    | 'gap_drag_words'
    | 'matching_text_pairs'
    | 'matching_zone_drop'
    | 'ordering'
    | 'speak'
    | 'write';

export type QuestionTypeLocale = 'en' | 'vi';

export interface QuestionTypeLabelEntry {
    slug: QuestionTypeEnumSlug;
    /** English display name — also the fallback when locale is missing. */
    en: string;
    /** Vietnamese display name. */
    vi: string;
    /** Chip / gutter colour. */
    color: string;
}

/**
 * Distinct, scannable labels. Gap variants use short unique stems so
 * typing / dropdown / word-bank / drag-words are easy to tell apart.
 *
 * Colours walk the hue wheel (orange → yellow → green → cyan → blue → violet →
 * pink → red → neutral) instead of grouping by family, because same-family
 * tints were indistinguishable in the import gutter.
 */
export const QUESTION_TYPE_LABEL_CATALOG: QuestionTypeLabelEntry[] = [
    { slug: 'set', en: 'Set', vi: 'set', color: '#f97316' },
    { slug: 'passage', en: 'Passage', vi: 'Đoạn văn', color: '#0ea5e9' },
    { slug: 'audio', en: 'Audio', vi: 'Âm thanh', color: '#8b5cf6' },
    { slug: 'image', en: 'Image', vi: 'Hình ảnh', color: '#ec4899' },
    { slug: 'scq', en: 'Single Choice', vi: 'Chọn một đáp án', color: '#16a34a' },
    { slug: 'mcq', en: 'Multiple Choice', vi: 'Chọn nhiều đáp án', color: '#0d9488' },
    { slug: 'true_false', en: 'True/False', vi: 'Đúng/Sai', color: '#84cc16' },
    { slug: 'gap_typing', en: 'Gap Typing', vi: 'Điền trống gõ', color: '#dc2626' },
    { slug: 'gap_dropdown', en: 'Gap Dropdown', vi: 'Điền trống dropdown', color: '#92400e' },
    { slug: 'gap_word_bank', en: 'Word Bank', vi: 'Ngân hàng từ', color: '#0891b2' },
    { slug: 'gap_drag_words', en: 'Drag Words', vi: 'Kéo thả từ', color: '#c026d3' },
    { slug: 'matching_text_pairs', en: 'Matching Text', vi: 'Nối cặp chữ', color: '#1e40af' },
    { slug: 'matching_zone_drop', en: 'Matching Zones', vi: 'Kéo thả vùng', color: '#6366f1' },
    { slug: 'ordering', en: 'Ordering', vi: 'Sắp xếp', color: '#eab308' },
    { slug: 'speak', en: 'Speaking', vi: 'Nói', color: '#f43f5e' },
    { slug: 'write', en: 'Writing', vi: 'Viết', color: '#475569' },
];

/**
 * EAA-2026: display names for the legacy QT_* types the Hub serves from
 * ``GET /content/ai-types/`` (keyed by ``AIContentType.code``). The API ``name`` is
 * English-only and PTE-styled, so the UI reads these instead. Typed as a full
 * ``Record<QuickCreateCode, …>`` so a new Quick Create code without a label fails tsc.
 */
export const QUICK_CREATE_TYPE_LABELS: Record<QuickCreateCode, { en: string; vi: string }> = {
    QT_FILL_BLANK_DRAG: { en: 'Fill in the Blanks (Type or Drag)', vi: 'Điền từ vào chỗ trống (gõ hoặc kéo thả)' },
    QT_IMAGE_WORD: { en: 'Look at Image and Write Answer', vi: 'Nhìn tranh và viết câu trả lời' },
    QT_IMAGE_YESNO: { en: 'Look at Image - Yes/No', vi: 'Nhìn tranh – Có/Không' },
    QT_LISTEN_COLOR_OBJECTS: { en: 'Listen & Color', vi: 'Nghe và tô màu' },
    QT_LISTEN_CONV_TICK: { en: 'Listen to a Conversation and Tick', vi: 'Nghe hội thoại và đánh dấu' },
    QT_LISTEN_DICTATION: { en: 'Write from Dictation', vi: 'Nghe – chép chính tả' },
    QT_LISTEN_HIGHLIGHT_INCORRECT_WORDS: { en: 'Highlight Incorrect Words', vi: 'Nghe – tìm từ sai' },
    QT_LISTEN_HIGHLIGHT_SUMMARY: { en: 'Highlight Correct Summary', vi: 'Nghe – chọn tóm tắt đúng' },
    QT_LISTEN_MATCH_LINE: { en: 'Listening - Match with Line', vi: 'Nghe – nối' },
    QT_LISTEN_MCQ: { en: 'Listening - Multiple Choice', vi: 'Nghe – chọn một đáp án' },
    QT_LISTEN_MCQ_MULTI: { en: 'Listening - Multiple Choice Multi', vi: 'Nghe – chọn nhiều đáp án' },
    QT_LISTEN_MISSING_WORD: { en: 'Select Missing Word', vi: 'Nghe – chọn từ còn thiếu' },
    QT_LISTEN_SUMMARIZE: { en: 'Listen & Summarize', vi: 'Nghe và tóm tắt' },
    QT_LISTEN_WRITE: { en: 'Listening - Fill in the Blanks', vi: 'Nghe – điền từ vào chỗ trống' },
    QT_LISTEN_WRITE_LETTER: { en: 'Listening - Write Letter', vi: 'Nghe – viết chữ cái' },
    QT_LOOK_READ_TICK_CROSS: { en: 'Look, Read - Tick/Cross', vi: 'Nhìn, đọc – đánh dấu đúng/sai' },
    QT_MATCHING: { en: 'Read and Match', vi: 'Đọc và nối' },
    QT_PARA_FILL_GIVEN: { en: 'Reading - Fill in the Blanks (Drag & Drop)', vi: 'Đọc – kéo thả từ vào chỗ trống' },
    QT_PASSAGE_MCQ: { en: 'Reading - Multiple Choice', vi: 'Đọc – chọn một đáp án' },
    QT_PIC_LETTER_WORD: { en: 'Picture - Letter/Word', vi: 'Nhìn tranh – viết chữ cái/từ' },
    QT_READ_MCQ_MULTI: { en: 'Reading - Multiple Choice Multi', vi: 'Đọc – chọn nhiều đáp án' },
    QT_READ_REORDER: { en: 'Re-order Paragraphs', vi: 'Đọc – sắp xếp đoạn văn' },
    QT_READ_WRITE_OWN: { en: 'Writing - Fill in the Blanks', vi: 'Viết – điền từ vào chỗ trống' },
    QT_SPEAK_ANSWER_SHORT_QUESTION: { en: 'Answer Short Question', vi: 'Nói – trả lời câu hỏi ngắn' },
    QT_SPEAK_READ_ALOUD: { en: 'Read Aloud', vi: 'Nói – đọc to' },
    QT_SPEAK_REPEAT_SENTENCE: { en: 'Repeat Sentence', vi: 'Nói – nhắc lại câu' },
    QT_SPEAK_RESPOND_SITUATION: { en: 'Respond to a Situation', vi: 'Nói – phản hồi tình huống' },
    QT_SPEAK_RETELL_LECTURE: { en: 'Re-Tell Lecture', vi: 'Nói – kể lại bài nghe' },
    QT_SPEAK_SUMMARIZE_GROUP_DISCUSSION: { en: 'Summarize Group Discussion', vi: 'Nói – tóm tắt thảo luận nhóm' },
    QT_VERB_TENSE: { en: 'Rewrite Sentence with Given Words', vi: 'Viết lại câu với từ cho sẵn' },
    QT_WRITE_ESSAY: { en: 'Writing - Write Essay', vi: 'Viết – viết bài luận' },
    QT_WRITE_SUMMARIZE_WRITTEN_TEXT: { en: 'Writing - Summarize Written Text', vi: 'Viết – tóm tắt văn bản' },
};

/** Hub slug (``qt-listen-mcq``) or code (``QT_LISTEN_MCQ``) → Quick Create label entry. */
function quickCreateLabelEntry(slugOrCode: string): { en: string; vi: string } | undefined {
    const code = (slugOrCode || '').trim().toUpperCase().replace(/-/g, '_');
    return (QUICK_CREATE_TYPE_LABELS as Record<string, { en: string; vi: string }>)[code];
}

const FALLBACK_COLOR = '#94a3b8';

const BY_SLUG = Object.fromEntries(
    QUESTION_TYPE_LABEL_CATALOG.map((entry) => [entry.slug, entry]),
) as Record<QuestionTypeEnumSlug, QuestionTypeLabelEntry>;

export function normalizeQuestionTypeLocale(locale?: string): QuestionTypeLocale {
    return locale?.toLowerCase().startsWith('vi') ? 'vi' : 'en';
}

/** Human-readable name for an Agent QuestionTypeEnum slug. */
export function getQuestionTypeLabel(slug: string, locale?: string): string {
    // EAA-2026: fall back to the QT_* table before returning the slug unchanged.
    const entry = BY_SLUG[slug as QuestionTypeEnumSlug] ?? quickCreateLabelEntry(slug);
    if (!entry) return slug;
    return normalizeQuestionTypeLocale(locale) === 'vi' ? entry.vi : entry.en;
}

/**
 * Heading for a prompt form's question type.
 *
 * EAA-2031: the forms used `aiContentType?.name ?? questionType`, so an embed that received
 * no content-type row showed the raw slug (`scq`, `ordering`) as its title. The shared
 * catalog label, in the active locale, wins; the row's own name and then the slug are
 * fallbacks for types the catalog does not know.
 */
export function getFormTypeDisplayName(
    questionType: string | undefined,
    contentTypeName?: string | null,
    locale?: string,
): string {
    if (questionType && hasQuestionTypeLabel(questionType)) {
        return getQuestionTypeLabel(questionType, locale);
    }
    return contentTypeName || questionType || '';
}

/** True when the shared catalog has a display name for this slug/code. */
export function hasQuestionTypeLabel(slug: string): boolean {
    return Boolean(BY_SLUG[slug as QuestionTypeEnumSlug] ?? quickCreateLabelEntry(slug));
}

/** Stable colour for an Agent QuestionTypeEnum slug. */
export function getQuestionTypeColor(slug: string): string {
    return BY_SLUG[slug as QuestionTypeEnumSlug]?.color ?? FALLBACK_COLOR;
}

export function getQuestionTypeLabelCatalog(locale?: string): Array<{ slug: string; label: string }> {
    const lang = normalizeQuestionTypeLocale(locale);
    return QUESTION_TYPE_LABEL_CATALOG.map((entry) => ({
        slug: entry.slug,
        label: lang === 'vi' ? entry.vi : entry.en,
    }));
}

export function isQuestionTypeEnumSlug(slug: string): slug is QuestionTypeEnumSlug {
    return slug in BY_SLUG;
}
